import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { BarChart2, Clock, Activity, Zap, Settings } from 'lucide-react';
import ModelConfigSimple from '../../components/agents/ModelConfigSimple';

const PerformancePage = () => {
  const [activeTab, setActiveTab] = useState("metrics");

  return (
    <div className="container py-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Agent Performance</h1>
      
      <Tabs defaultValue="metrics" value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="metrics">Key Metrics</TabsTrigger>
          <TabsTrigger value="latency">Latency Analysis</TabsTrigger>
          <TabsTrigger value="usage">Usage Statistics</TabsTrigger>
          <TabsTrigger value="model-config" className="flex items-center gap-1.5">
            <Settings className="h-4 w-4" />
            Model Configuration
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="metrics" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <MetricCard 
              title="Average Response Time" 
              value="843ms" 
              change="-12.5%" 
              trend="down" 
              icon={<Clock className="h-5 w-5" />}
            />
            <MetricCard 
              title="Success Rate" 
              value="98.2%" 
              change="+1.4%" 
              trend="up" 
              icon={<Activity className="h-5 w-5" />}
            />
            <MetricCard 
              title="Performance Score" 
              value="87/100" 
              change="+3.2" 
              trend="up" 
              icon={<BarChart2 className="h-5 w-5" />}
            />
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Performance Trends</CardTitle>
              <CardDescription>30 day agent performance statistics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80 flex items-center justify-center border rounded-md">
                <p className="text-muted-foreground">Performance chart visualization will be displayed here</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="latency">
          <Card>
            <CardHeader>
              <CardTitle>Latency Analysis</CardTitle>
              <CardDescription>Detailed breakdown of agent response times</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80 flex items-center justify-center border rounded-md">
                <p className="text-muted-foreground">Latency charts and analysis will be displayed here</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="usage">
          <Card>
            <CardHeader>
              <CardTitle>Usage Statistics</CardTitle>
              <CardDescription>Agent usage patterns and request volumes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80 flex items-center justify-center border rounded-md">
                <p className="text-muted-foreground">Usage statistics will be displayed here</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="model-config">
          <ModelConfigSimple />
        </TabsContent>
      </Tabs>
      
      <Card>
        <CardHeader>
          <CardTitle>Performance Optimization Suggestions</CardTitle>
          <CardDescription>AI-generated recommendations to improve agent performance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <OptimizationItem 
              title="Reduce prompt complexity"
              description="Current prompts have high token counts. Streamlining prompts could reduce latency by ~15%."
              impact="Medium"
            />
            <OptimizationItem 
              title="Implement result caching"
              description="Frequently requested information could be cached to improve response times for common queries."
              impact="High"
            />
            <OptimizationItem 
              title="Optimize database queries"
              description="Database lookups are adding significant latency. Consider indexing frequently queried fields."
              impact="Medium"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper components
const MetricCard = ({ title, value, change, trend, icon }) => {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <h3 className="text-2xl font-bold mt-1">{value}</h3>
            <div className={`flex items-center mt-1 text-sm ${trend === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
              <span>{change}</span>
              <span className="ml-1">from last period</span>
            </div>
          </div>
          <div className="p-2 bg-muted rounded-md">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const OptimizationItem = ({ title, description, impact }) => {
  return (
    <div className="p-4 border rounded-lg">
      <div className="flex items-start justify-between">
        <div className="flex gap-3">
          <div className="mt-0.5">
            <Zap className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <h4 className="font-medium">{title}</h4>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          </div>
        </div>
        <div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full 
            ${impact === 'High' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' : 
            impact === 'Medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' : 
            'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'}`}>
            {impact} impact
          </span>
        </div>
      </div>
    </div>
  );
};

export default PerformancePage;